// ═══ 인보이스 드래프트 sessionStorage 저장/복원 — useInvoiceState.js 에서 분리 ═══
// Phase 2 시동 (2026-05-08)
// 분석 보고서: docs/analysis/use-invoice-state-split-2026-05-08.md
//
// CompanyListPanel 회사 전환 시 작업 보존 — autodoc_invoice_temp_{cid} TTL 30분.
// 파일 (File / blob URL) 은 직렬화 한계로 제외, hadPhotos 플래그만 저장.
// 예외: pendingOcrFile(쇼링 OCR 원본, #364) 은 1.5MB 이하면 dataURL 로 함께 저장/복원.
import { compressFiles } from './imageCompress';

var MAX_PENDING_OCR_BYTES = 1.5 * 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise(function(resolve, reject) {
    var reader = new FileReader();
    reader.onload = function() { resolve(reader.result); };
    reader.onerror = function() { reject(reader.error || new Error('파일 읽기 실패')); };
    reader.readAsDataURL(file);
  });
}

function dataUrlToFile(dataUrl, name) {
  var comma = dataUrl.indexOf(',');
  var meta = dataUrl.slice(0, comma);
  var mimeMatch = /data:([^;]+);base64/.exec(meta);
  var mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  var binary = atob(dataUrl.slice(comma + 1));
  var bytes = new Uint8Array(binary.length);
  for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name || 'screenshot.jpg', { type: mime });
}

// state 객체 → sessionStorage 직렬화 (비동기 — pendingOcrFile 압축/dataURL 변환 때문)
// state: { carRows, selBuyer, savedInvoiceId, consignee, vesselName, fromPort, toPort,
//          departDate, bkNo, containerNo, sealNo, entryPoint, currency, exchangeRate,
//          incoterms, freight, insurance, freightCurrency, insuranceCurrency,
//          shippingType, toEmail, invoiceNo, invoiceDate, pendingOcrFile }
export async function saveInvoiceDraft(cid, state) {
  if (!cid) return;
  var carRows = state.carRows || [];
  var hasContent = carRows.some(function(r){ return r.carNo || r.vin || r.price || r.weight; });
  if (!hasContent) {
    try { sessionStorage.removeItem('autodoc_invoice_temp_' + cid); } catch (e) {}
    return;
  }
  // 사진 있었는지 플래그 (복원 시 사용자 안내용)
  var hadPhotos = carRows.some(function(r){ return (r.photos && r.photos.length > 0) || r.photoUrl || r.photo; });
  var clean = carRows.map(function(r){
    var c = {};
    Object.keys(r).forEach(function(k){
      if (k === 'photo' || k === 'photos' || k === 'photoUrl' || k === 'photoUrls') return;
      c[k] = r[k];
    });
    return c;
  });
  // 쇼링 OCR 대기 원본 — 재진입 시에도 유지 (#364). 1.5MB 초과면 저장 생략.
  var pendingOcr = null;
  if (state.pendingOcrFile && state.pendingOcrFile.file) {
    try {
      var compressed = await compressFiles([state.pendingOcrFile.file]);
      var f = (compressed && compressed[0]) || state.pendingOcrFile.file;
      if (f && f.size <= MAX_PENDING_OCR_BYTES) {
        var dataUrl = await fileToDataUrl(f);
        pendingOcr = { name: state.pendingOcrFile.name || f.name || 'screenshot.jpg', dataUrl: dataUrl };
      }
    } catch (e) { /* 변환 실패 — 대기 원본 저장만 생략, 드래프트 본체는 계속 */ }
  }
  try {
    sessionStorage.setItem('autodoc_invoice_temp_' + cid, JSON.stringify({
      ts: Date.now(), carRows: clean, hadPhotos: hadPhotos,
      selBuyer: state.selBuyer, savedInvoiceId: state.savedInvoiceId,
      consignee: state.consignee, vesselName: state.vesselName,
      fromPort: state.fromPort, toPort: state.toPort, departDate: state.departDate,
      bkNo: state.bkNo, containerNo: state.containerNo, sealNo: state.sealNo, entryPoint: state.entryPoint,
      currency: state.currency, exchangeRate: state.exchangeRate, incoterms: state.incoterms,
      freight: state.freight, insurance: state.insurance,
      freightCurrency: state.freightCurrency, insuranceCurrency: state.insuranceCurrency,
      shippingType: state.shippingType, toEmail: state.toEmail,
      invoiceNo: state.invoiceNo, invoiceDate: state.invoiceDate,
      pendingOcr: pendingOcr
    }));
  } catch (e) { /* sessionStorage 용량 초과 등 — 드래프트 저장 실패는 조용히 무시 */ }
}

// sessionStorage → state setter 호출 (TTL 30분 만료 시 false 반환)
// deps: { setCarRows, setSelBuyer, setConsignee, setVesselName, setFromPort, setToPort,
//          setDepartDate, setBkNo, setContainerNo, setSealNo, setEntryPoint,
//          setCurrency, setExchangeRate, setIncoterms, setFreight, setInsurance,
//          setFreightCurrency, setInsuranceCurrency, setShippingType, setToEmail,
//          setInvoiceNo, setInvoiceDate, setSavedInvoiceId, loadInvoice, showToast }
export function restoreInvoiceDraft(cid, deps) {
  if (!cid) return false;
  try {
    var saved = sessionStorage.getItem('autodoc_invoice_temp_' + cid);
    if (!saved) return false;
    var p = JSON.parse(saved);
    if (!p.ts || Date.now() - p.ts > 1800000) {
      sessionStorage.removeItem('autodoc_invoice_temp_' + cid);
      return false;
    }
    if (p.carRows && p.carRows.length > 0) deps.setCarRows(p.carRows);
    if (p.selBuyer) deps.setSelBuyer(p.selBuyer);
    if (p.consignee !== undefined) deps.setConsignee(p.consignee);
    if (p.vesselName !== undefined) deps.setVesselName(p.vesselName);
    if (p.fromPort !== undefined) deps.setFromPort(p.fromPort);
    if (p.toPort !== undefined) deps.setToPort(p.toPort);
    if (p.departDate !== undefined) deps.setDepartDate(p.departDate);
    if (p.bkNo !== undefined) deps.setBkNo(p.bkNo);
    if (p.containerNo !== undefined) deps.setContainerNo(p.containerNo);
    if (p.sealNo !== undefined) deps.setSealNo(p.sealNo);
    if (p.entryPoint !== undefined) deps.setEntryPoint(p.entryPoint);
    if (p.currency) deps.setCurrency(p.currency);
    if (p.exchangeRate !== undefined) deps.setExchangeRate(p.exchangeRate);
    if (p.incoterms) deps.setIncoterms(p.incoterms);
    if (p.freight !== undefined) deps.setFreight(p.freight);
    if (p.insurance !== undefined) deps.setInsurance(p.insurance);
    if (p.freightCurrency) deps.setFreightCurrency(p.freightCurrency);
    if (p.insuranceCurrency) deps.setInsuranceCurrency(p.insuranceCurrency);
    if (p.shippingType) deps.setShippingType(p.shippingType);
    if (p.toEmail !== undefined) deps.setToEmail(p.toEmail);
    if (p.invoiceNo) deps.setInvoiceNo(p.invoiceNo);
    if (p.invoiceDate) deps.setInvoiceDate(p.invoiceDate);
    if (p.savedInvoiceId) {
      deps.setSavedInvoiceId(p.savedInvoiceId);
      if (deps.loadInvoice) deps.loadInvoice(p.savedInvoiceId);
    }
    if (p.pendingOcr && p.pendingOcr.dataUrl && deps.setPendingOcrFile) {
      try {
        var restoredFile = dataUrlToFile(p.pendingOcr.dataUrl, p.pendingOcr.name);
        deps.setPendingOcrFile({ file: restoredFile, name: p.pendingOcr.name || restoredFile.name });
      } catch (e) { /* 복원 실패 — pending 없이 진행 */ }
    }
    if (p.hadPhotos && !p.savedInvoiceId && deps.showToast) {
      setTimeout(function(){
        deps.showToast('📷 사진은 직렬화 한계로 복원되지 않았습니다. 다시 첨부해주세요', 'warn');
      }, 300);
    }
    return true;
  } catch (e) { return false; }
}
